import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
// A4. Injected by esbuild `define` in angular.json; exists only in configurations
// that set it (today: `local`). The `typeof` guard in the providers below keeps
// every other target compiling and running.
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { AppCurrencyPipe } from '@app/_shared/pipes/app-currency.pipe';
import { FirstLetterUppercasePipe } from '@app/_shared/pipes/first-letter-uppercase.pipe';
import { IdleMarkedRoutesPreloadingStrategy } from '@app/_shared/strategies/idle-marked-routes-preloading.strategy';
import { provideKeyboardAwareScrollStrategy } from '@app/_shared/strategies/keyboard-aware-block-scroll.strategy';
import { rootStoreProviders } from '@app/_store/root-store';
import { DbConfig } from '@core/configs/db.config';
import { FuseConfig } from '@core/configs/fuse-config';
import { httpInterceptors } from '@core/interceptors';
import { AppToastComponent } from '@core/notifications/app-toast/app-toast.component';
import { provideAppInitializer } from '@core/providers/app-initializer.provider';
import { provideGtagInitializer } from '@core/providers/gtag.provider';
import { provideKeyboardNavigationMode } from '@core/providers/keyboard-navigation.provider';
import { provideMaterialDateAdapter } from '@core/providers/material-date-adapter.provider';
import { provideSentry } from '@core/providers/sentry.provider';
import {
  provideStaleChunkReloadReset,
  withStaleChunkReload,
} from '@core/providers/stale-chunk-reload.provider';
import { provideVisualViewportHeight } from '@core/providers/visual-viewport.provider';
import { PageTitleStrategy } from '@core/services/page-title.strategy';
import { provideTokens } from '@core/tokens/tokens-providers';
import { environment } from '@environments/environment';
import { provideFuse } from '@fuse/fuse.provider';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaLoaderService,
  ReCaptchaV3Service,
} from 'ng-recaptcha-2';
import { provideIndexedDb } from 'ngx-indexed-db';
import { provideNgxStripe } from 'ngx-stripe';
import { provideToastr } from 'ngx-toastr';

import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideTokens(),
    // The store registers BEFORE anything that might read it: environment
    // initializers run in provider order, and both provideAppInitializer
    // (AppService/AuthService chains) and provideFuse (splash screen selects
    // appActions in its constructor) used to run before the states existed —
    // every such selector fired the dev-mode "feature name does not exist in
    // the state" warning and read undefined until the first action.
    ...rootStoreProviders,
    provideAppInitializer(),
    provideAnimationsAsync(),
    // No `withFetch` here on purpose (A13, rolled back 2026-08-18): it passed locally
    // (same-origin via the proxy) but broke the stand — cross-origin my-dev → app-dev
    // requests died on a CORS preflight the XHR transport never triggers. Bring fetch
    // back only after backend/infra confirms preflight parity for it.
    provideHttpClient(withXhr(), withInterceptors(httpInterceptors)),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      // R19. Only routes marked `data: { preload: true }`, and only at idle. The strategy's own
      // comment carries the numbers that rule out `PreloadAllModules`.
      withPreloading(IdleMarkedRoutesPreloadingStrategy),
      // POC-9609. A tab opened before a deploy asks for chunks the deploy
      // renamed; the first unvisited lazy screen then fails silently. Reload
      // onto the target URL, once.
      withStaleChunkReload()
    ),
    // R36. Zoneless everywhere. The road here: 19 zone-reliant writes converted
    // to signals (gate 1), a detector script keeping them at zero (gate 2), a
    // PG_ZONELESS define soaked on the local stand (gate 3), a full autonomous
    // feature walk with zero CD failures (gate 4), and a manual auth/quiz/
    // checkout QA on the dev stand (gate 5). zone.js is out of the polyfills:
    // change detection is driven by signals and template events alone.
    provideZonelessChangeDetection(),
    provideKeyboardAwareScrollStrategy(),
    provideVisualViewportHeight(),
    provideKeyboardNavigationMode(),
    provideStaleChunkReloadReset(),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }, // temporary fix for toastr
    },
    provideIndexedDb(DbConfig),
    // R83. `RecaptchaV3Module` was the last NgModule shim in the bootstrap, and all it ever
    // provided was these two services — it declares nothing. `ReCaptchaV3Service` was already
    // listed here directly, so the whole `importProvidersFrom` existed for `RecaptchaLoaderService`,
    // which is a plain `@Injectable()` with no `providedIn: 'root'` and therefore does have to be
    // provided by hand. Both are named outright now, and the module class drops out of the bundle.
    ReCaptchaV3Service,
    RecaptchaLoaderService,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.googleCaptchaKey,
    },
    {
      provide: 'googleTagManagerId',
      useValue: environment.googleTagKey,
    },
    provideGtagInitializer(),
    provideNgxStripe(environment.stripePublishableKey),
    provideToastr({
      enableHtml: true,
      toastClass: 'ngx-toastr app-toast',
      // Renders itself instead of waiting for a tick that a crash elsewhere can
      // take with it — see app-toast.component.ts.
      toastComponent: AppToastComponent,
    }),
    provideMaterialDateAdapter(),
    // Pipes
    DatePipe,
    CurrencyPipe,
    AppCurrencyPipe,
    FirstLetterUppercasePipe,
    // Sentry
    provideSentry(),
    // Fuse
    provideFuse(FuseConfig),
  ],
};
